import { useCallback, useEffect, useReducer, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ApiError, type ApiClient } from '@enshield/api-client';
import type { EvidencePolicyDto, PublicClaimStatusDto } from '@enshield/domain';
import { Button, Card, Field, Input, Notice, Select, Textarea } from '@enshield/ui';
import { DEFAULT_EVIDENCE_POLICY, formatBytes, uploadEvidenceWithProgress, validateEvidenceFile, type EvidenceUploader } from './evidence';
import { createEvidenceQueueItems, evidenceQueueReducer, validateEvidenceSelection, type EvidenceQueueItem } from './evidence-queue';
import { PUBLIC_CLAIM_ENDPOINTS } from './contracts';
import { claimMotion } from './motion';
import { focusClaimHeading } from './focus';
import { ClaimGuidance } from './ClaimGuidance';
import { consumeTrackingTokenFromUrl, storeTrackingToken, tokenFromPastedValue } from './token-handoff';
import {
  ClaimProgress,
  ClaimStepActions,
  ClaimStepPanel,
  formatClaimMoney,
  useClaimWizard,
  useStepDirection,
} from './wizard';
import './wizard.css';

export interface PublicClaimComponentProps {
  apiClient: ApiClient;
  supportEmail: string;
  publicBaseUrl: string;
}

interface VerifiedLine { id: string; title: string; quantity: number; sku?: string | null; unitPrice: number; totalPrice: number; }
interface VerifiedOrder {
  orderNumber: string;
  currency: string;
  /**
   * The claim form the server matched, echoed back so submission targets the
   * store verification already resolved rather than re-running a cross-store
   * match that could answer differently on the second request.
   */
  formSlug?: string;
  /** The merchant the order was found at, shown so the shopper can confirm it. */
  storeName?: string | null;
  items: VerifiedLine[];
}

function messageFor(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function ClaimFrame({ title, intro, supportEmail, children, hideHeading = false }: { title: string; intro: string; supportEmail: string; children: React.ReactNode; hideHeading?: boolean }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (!hideHeading) focusClaimHeading(heading.current, title === 'Claim submitted'); }, [hideHeading, title]);
  return (
    <div className="claim-frame">
      {hideHeading ? null : <div className="claim-heading">
        <p className="claim-kicker">Enshield claim support</p>
        <h1 ref={heading} tabIndex={-1}>{title}</h1>
        <p>{intro}</p>
      </div>}
      <div className="claim-stack">{children}</div>
      <p className="claim-support">Need help? <a href={`mailto:${supportEmail}`}>{supportEmail}</a></p>
    </div>
  );
}

function EvidenceList({ claim }: { claim: PublicClaimStatusDto }) {
  return claim.evidence.length ? (
    <ul className="claim-list">{claim.evidence.map((item) => <li key={`${item.fileName}-${item.uploadedAt ?? ''}`}><span>{item.fileName}</span><small>{formatBytes(item.byteSize)}</small></li>)}</ul>
  ) : <p className="claim-empty">No evidence attached yet. Photos, delivery notices, or receipts can be added here.</p>;
}

function QueuedEvidencePreview({ file }: { file: File }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return;
    const next = URL.createObjectURL(file);
    setSrc(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return src
    ? <img className="claim-evidence-thumb" src={src} alt="" />
    : <span className="claim-evidence-filetype" aria-hidden>{file.type === 'application/pdf' ? 'PDF' : 'FILE'}</span>;
}

function QueuedEvidence({ items, onRemove, onRetry, removable }: {
  items: EvidenceQueueItem[];
  onRemove: (id: string) => void;
  onRetry?: (item: EvidenceQueueItem) => void;
  removable: boolean;
}) {
  if (!items.length) return null;
  return <ul className="claim-evidence-queue" aria-label="Selected evidence">
    {items.map((item) => <li key={item.id}>
      <QueuedEvidencePreview file={item.file} />
      <span><strong>{item.file.name}</strong><small>{formatBytes(item.file.size)} · {item.status}</small></span>
      {item.status === 'uploading' ? <progress aria-label={`Uploading ${item.file.name}`} value={item.progress} max={100}>{item.progress}%</progress> : null}
      {item.error ? <small className="claim-evidence-error" role="alert">{item.error}</small> : null}
      {item.status === 'failed' && onRetry ? <button type="button" aria-label={`Retry ${item.file.name}`} onClick={() => onRetry(item)}>Retry</button> : null}
      {removable && item.status !== 'uploading' ? <button type="button" aria-label={`Remove ${item.file.name}`} onClick={() => onRemove(item.id)}>Remove</button> : null}
    </li>)}
  </ul>;
}

function timelineEventLabel(eventType: string): string {
  // Unmapped types fall through to a de-underscored lowercase form, which for
  // APPROVED_AMOUNT_NOTICE reads "approved amount notice" — accurate but
  // clumsy on the one entry that tells a shopper their payout moved.
  return ({ CREATED: 'Claim created', SUBMITTED: 'Submitted', STATUS_CHANGE: 'Status update', EVIDENCE_REQUESTED: 'Evidence requested', EVIDENCE_ADDED: 'Evidence added', APPROVED_AMOUNT_NOTICE: 'Approved amount updated' } as Record<string, string>)[eventType]
    ?? eventType.replace(/_/g, ' ').toLowerCase();
}

function guidanceForStatus(claim: PublicClaimStatusDto): string | null {
  switch (claim.status) {
    case 'NEW': case 'PENDING': return "We've received your claim and it's in our review queue. We'll update this page as soon as there's news.";
    case 'AWAITING_EVIDENCE': return 'We need more information to continue reviewing this claim. Please attach the requested files below.';
    case 'APPROVED': return claim.approvedAmount === null ? "Your claim was approved. We'll follow up with payment or replacement details." : `Your claim was approved for ${claim.approvedAmount.toFixed(2)} ${claim.currency}. We'll follow up with payment or replacement details.`;
    case 'DENIED': return 'This claim was denied. If you have additional evidence, attach it below and we will take another look.';
    case 'COMPLETED': return 'This claim is complete. Thank you.';
    case 'CANCELLED': return 'This claim was cancelled.';
    default: return null;
  }
}

/**
 * One claim wizard, used by both public routes.
 *
 * `formSlug` is the merchant context. When a shopper arrives from their
 * merchant — a Shopify block, a WooCommerce shortcode, an order email — the
 * context is already known and is passed in.
 *
 * When someone arrives at /file-a-claim from Enshield.com there is no context,
 * and the wizard no longer asks for one. It used to: step one opened with
 * "Where did you purchase from?" and a merchant typeahead. That question was
 * wrong twice over. Shoppers know the brand on the package, not the store name
 * as Enshield records it, so the search failed for people whose order was
 * perfectly valid — and a public endpoint that lists merchants by prefix is a
 * directory of every business on the platform to anyone who asks.
 *
 * Both routes now ask for exactly the same two things: order number and the
 * email used at checkout. The server matches that pair across every eligible
 * store and requires exactly one hit, so the shopper never names a merchant
 * and never types a claim form code.
 */

/** A store a shopper may file a claim against, as the public lookup returns it. */
export interface PublicClaimStore {
  /** The claim form's public slug — the same identifier merchant links carry. */
  slug: string;
  merchantName: string;
  storeName: string;
  domain: string;
}

export function ClaimSubmission({ apiClient, formSlug: fixedFormSlug, supportEmail, publicBaseUrl, artworkBaseUrl, uploadEvidence, hideHeading = false }: PublicClaimComponentProps & { formSlug?: string; artworkBaseUrl?: string; uploadEvidence?: EvidenceUploader; hideHeading?: boolean }) {
  /*
   * The merchant, once the server has resolved it.
   *
   * On a merchant link this is fixed up front. On the generic route it is
   * unknown until verification matches the order number and email to exactly
   * one eligible store, and the slug that comes back is what submission then
   * targets — so both routes converge on the same server contract, and the
   * shopper names a merchant on neither.
   */
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null);
  const formSlug = fixedFormSlug ?? resolvedSlug ?? '';
  const needsStore = !fixedFormSlug;
  const reducedMotion = useReducedMotion() ?? false;

  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('LOST');
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState<VerifiedOrder | null>(null);
  const [selected, setSelected] = useState<Record<string, number>>({});
  /**
   * Whether the shopper has chosen items since the last lookup.
   *
   * A ref rather than derived from `selected`, because `verify()` needs to know
   * what was true BEFORE it overwrites the selection, and reading state inside
   * an async handler gives whatever the closure captured.
   */
  const hadSelection = useRef(false);
  /**
   * Whether a lookup is already in flight.
   *
   * Same reasoning as `hadSelection` above: a guard that reads state reads
   * whatever the closure captured, so two Enter presses inside one React batch
   * both see the stale value and both fire.
   */
  const verifyInFlight = useRef(false);
  const [busy, setBusy] = useState<'verify' | 'submit' | 'upload' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ claim: PublicClaimStatusDto; trackingToken: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [policy, setPolicy] = useState<EvidencePolicyDto>(DEFAULT_EVIDENCE_POLICY);
  const [queue, dispatchQueue] = useReducer(evidenceQueueReducer, []);

  useEffect(() => {
    let active = true;
    Promise.resolve(apiClient.get<EvidencePolicyDto>(PUBLIC_CLAIM_ENDPOINTS.evidencePolicy))
      .then((next) => { if (active && next) setPolicy(next); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [apiClient]);

  const selectEvidence = useCallback((files: Iterable<File>) => {
    const selection = validateEvidenceSelection(files, queue, policy, { count: 0, totalBytes: 0 });
    setError(selection.errors.length ? selection.errors.join(' ') : null);
    if (selection.accepted.length) {
      dispatchQueue({ type: 'add', items: createEvidenceQueueItems(selection.accepted) });
    }
  }, [policy, queue]);

  const uploadQueuedItem = useCallback(async (
    item: EvidenceQueueItem,
    token: string,
    currentClaim: PublicClaimStatusDto,
  ) => {
    const form = new FormData();
    form.append('token', token);
    form.append('file', item.file);
    dispatchQueue({ type: 'uploading', id: item.id });
    try {
      const uploader = uploadEvidence ?? ((path: string, body: FormData, progress: (value: number) => void) => uploadEvidenceWithProgress<PublicClaimStatusDto>(apiClient.url(path), body, progress));
      const claim = await uploader(PUBLIC_CLAIM_ENDPOINTS.evidence, form, (progress) => dispatchQueue({ type: 'progress', id: item.id, progress }));
      dispatchQueue({ type: 'succeeded', id: item.id });
      // Clear this file's stale failure after a successful retry, without
      // dismissing an unrelated error raised by a different operation.
      if (item.error) setError((current) => current === item.error ? null : current);
      setResult((current) => current ? { ...current, claim } : current);
      return claim;
    } catch (cause) {
      const message = messageFor(cause, `Unable to attach ${item.file.name}.`);
      dispatchQueue({ type: 'failed', id: item.id, error: message });
      setError(message);
      return currentClaim;
    }
  }, [apiClient, uploadEvidence]);

  const verify = async () => {
    /*
     * Guarded against a second call while the first is in flight.
     *
     * The visible Continue button carries `nextBusy`, so it disables itself.
     * Implicit submission does not go through that button — the form's default
     * submit is a hidden control that must never be `disabled`, because a
     * disabled button is not a default button and Enter would stop working
     * entirely. So Enter pressed twice fired two lookups, and
     * `assertPublicOrderVerificationAllowed` 429s the second: the shopper
     * advances AND sees a red error, with both attempts debited from the form's
     * daily budget.
     */
    /*
     * A REF, not the `busy` state — twelve lines from the comment that says why.
     *
     * `busy` is the value this render's closure captured, and two Enter presses
     * arriving before React commits `setBusy('verify')` — held-Enter auto-repeat,
     * or any batched flush — both read the stale value and both pass. That is
     * verbatim the double lookup the guard was written to prevent. A ref is
     * written synchronously, so the second press sees it.
     */
    if (verifyInFlight.current) return;
    verifyInFlight.current = true;
    if (!orderNumber.trim() || !email.trim()) {
      verifyInFlight.current = false;
      setError('Enter your order number and email before verifying.');
      return;
    }
    setBusy('verify'); setError(null);
    try {
      /*
       * `formSlug` is sent only when the link supplied one. Omitting it is what
       * asks the server to match across every eligible store, so the generic
       * route must NOT send an empty string — that would be a slug that
       * resolves to no form and a 404 instead of a lookup.
       */
      const verified = await apiClient.post<VerifiedOrder>(PUBLIC_CLAIM_ENDPOINTS.verifyOrder, {
        ...(fixedFormSlug ? { formSlug: fixedFormSlug } : {}),
        orderNumber: orderNumber.trim(),
        email: email.trim(),
      });
      setOrder(verified);
      /*
       * The shopper's item choice is reset here, and that has to be SAID.
       *
       * The recovery panel promises "we will bring you back here", and the
       * auto-advance only fires from step one — so editing the order row after
       * choosing items two and three lands on the items step with item one
       * selected, Continue enabled, and nothing indicating the selection
       * changed. The reset itself is right: the items belong to the order that
       * was just looked up, and carrying a stale selection across a different
       * order would be worse.
       */
      if (hadSelection.current) setNotice('Your item selection was reset for this order.');
      hadSelection.current = false;
      // Remember which store the server matched, so submission targets it
      // rather than repeating the cross-store lookup.
      if (!fixedFormSlug && verified.formSlug) setResolvedSlug(verified.formSlug);
      setSelected(verified.items[0] ? { [verified.items[0].id]: 1 } : {});
      if (!verified.items.length) setError('This order has no eligible items to claim.');
    } catch (cause) { setError(messageFor(cause, 'Unable to verify that order. Please try again.')); }
    finally { verifyInFlight.current = false; setBusy(null); }
  };

  const submit = async () => {
    if (!order || !description.trim() || !Object.keys(selected).length) { setError('Verify your order, choose an item, and describe what happened.'); return; }
    if (order.items.some((item) => item.id in selected && (!Number.isInteger(selected[item.id]) || selected[item.id]! < 1 || selected[item.id]! > item.quantity))) { setError('Each claimed quantity must be between 1 and the ordered quantity.'); return; }
    setBusy('submit'); setError(null);
    try {
      const created = await apiClient.post<{ claim: PublicClaimStatusDto; trackingToken: string }>(PUBLIC_CLAIM_ENDPOINTS.submit, {
        formSlug, orderNumber: orderNumber.trim(), email: email.trim(), reason, description: description.trim(),
        // `in`, not truthiness: a cleared quantity is 0, and dropping it here
        // would submit a claim that does not match the review step the shopper
        // just confirmed. The guard above has already refused 0.
        items: order.items.filter((item) => item.id in selected).map((item) => ({ orderLineItemId: item.id, title: item.title, quantity: selected[item.id] })),
        customFields: {},
      });
      setResult(created);
      let currentClaim = created.claim;
      for (const item of queue.filter((entry) => entry.status === 'queued' || entry.status === 'failed')) {
        currentClaim = await uploadQueuedItem(item, created.trackingToken, currentClaim);
      }
    } catch (cause) { setError(messageFor(cause, 'Unable to submit the claim. Please try again.')); }
    finally { setBusy(null); }
  };

  const dropEvidence = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    selectEvidence(event.dataTransfer.files);
  };

  const upload = async (file?: File) => {
    if (!file || !result) return;
    /*
     * The FETCHED policy, not the compiled-in default.
     *
     * This page already loads the policy — the queue path two functions up
     * validates against it — and then the single-file path beside it validated
     * against `DEFAULT_EVIDENCE_POLICY`. Two validators, one screen, no reason
     * for them to be different ones.
     *
     * To be accurate about the blast radius, since an earlier version of this
     * comment was not: `readEvidencePolicy` on the API takes no store and no
     * organization, so there is no PER-MERCHANT policy today and the two can
     * currently disagree only through `EVIDENCE_MAX_FILE_BYTES`, which is
     * deployment configuration. Raise that and the compiled-in 10 MB refuses,
     * in the browser, a file the server would accept. The per-merchant case
     * this guards against is not reachable yet; the deployment one is.
     */
    const validation = validateEvidenceFile(file, policy);
    if (validation) { setError(validation); return; }
    const form = new FormData(); form.append('token', result.trackingToken); form.append('file', file);
    setBusy('upload'); setUploadProgress(0); setError(null);
    try {
      const uploader = uploadEvidence ?? ((path: string, body: FormData, progress: (value: number) => void) => uploadEvidenceWithProgress<PublicClaimStatusDto>(apiClient.url(path), body, progress));
      const claim = await uploader(PUBLIC_CLAIM_ENDPOINTS.evidence, form, setUploadProgress);
      setResult({ ...result, claim }); setNotice(`${file.name} was attached to your claim.`);
    } catch (cause) { setError(messageFor(cause, 'Unable to attach that file.')); }
    finally { setBusy(null); setUploadProgress(0); }
  };

  /* ---------------------------------------------------------------- wizard */

  const REASON_LABELS: Record<string, string> = {
    LOST: 'Lost in transit',
    DAMAGED: 'Damaged',
    STOLEN: 'Stolen',
    NOT_DELIVERED: 'Not delivered',
    OTHER: 'Other',
  };

  const wizard = useClaimWizard();
  const direction = useStepDirection(wizard.index);

  /*
   * Keyed on presence, not on truthiness.
   *
   * A cleared quantity field writes 0, and 0 is falsy — so an item the shopper
   * had ticked, and which still rendered as ticked, dropped straight out of
   * `selectedLines`. Clearing a box to retype it silently removed that item
   * from the claim, and the Next button stayed enabled because the validity
   * check was computed over the same filtered list. The submit path filtered
   * the same way, so the claim went in covering one item while the review step
   * behind it still showed two.
   */
  const selectedLines = order ? order.items.filter((item) => item.id in selected) : [];
  const quantitiesValid = selectedLines.every(
    (item) => Number.isInteger(selected[item.id]) && selected[item.id]! >= 1 && selected[item.id]! <= item.quantity,
  );

  /*
   * `order` becoming non-null is the SERVER's signal that verification passed —
   * advancing on it rather than on the click means a rejected lookup can never
   * move the customer forward.
   */
  useEffect(() => {
    if (order && order.items.length && wizard.index === 0) wizard.next();
    // Intentionally keyed on `order` alone. Including `wizard` would re-run
    // this on every step change and drag the customer back to step 2 each time
    // they navigated backwards. (No eslint-disable here: this package's config
    // does not load react-hooks, so the directive itself was the lint error.)
  }, [order]);

  if (result) return (
    <ClaimFrame title="Claim submitted" intro="Save your private tracking token so you can return for updates." supportEmail={supportEmail}>
      <ClaimGuidance step="done" artworkBaseUrl={artworkBaseUrl} />
      <motion.div
        data-testid="claim-submission-confirmation"
        data-motion="claim-confirmation"
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        initial={reducedMotion ? false : { opacity: 0, y: claimMotion.distance.feedback }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reducedMotion ? 0 : claimMotion.duration.interface, ease: claimMotion.ease.standard }}
      >
      <Card>
        <Notice tone="success">Your claim has been submitted.</Notice>
        {/* The customer-facing reference, never the internal id. */}
        <p className="claim-reference__label">Claim reference</p>
        <h2 className="claim-reference">{result.claim.reference}</h2>
        <p className="claim-token"><code>{result.trackingToken}</code></p>
        <p className="cw-legal">
          We have emailed a private tracking link to the address on your order. Keep your token
          confidential — it is what grants access to this claim.
        </p>
        <a className="claim-action" href={`${publicBaseUrl.replace(/\/$/, '')}/track-claim`} onClick={() => storeTrackingToken(result.trackingToken)}>Track this claim</a>
      </Card>
      <Card>
        <h2>Supporting evidence ({result.claim.evidenceCount})</h2><EvidenceList claim={result.claim} />
        <QueuedEvidence items={queue} removable={false} onRemove={() => undefined} onRetry={(item) => {
          dispatchQueue({ type: 'retry', id: item.id });
          void uploadQueuedItem(item, result.trackingToken, result.claim);
        }} />
        {error ? <Notice tone="error">{error}</Notice> : null}{notice ? <Notice tone="success">{notice}</Notice> : null}
        {busy === 'upload' ? <progress aria-label="Evidence upload progress" aria-valuenow={uploadProgress} value={uploadProgress} max={100}>{uploadProgress}%</progress> : null}
        {result.claim.canUploadEvidence ? <label className="claim-file">Attach a photo or document<input aria-label="Attach a photo or document" type="file" accept={policy.acceptedContentTypes.join(',')} disabled={busy === 'upload'} onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ''; void upload(file); }} /></label> : null}
      </Card>
      </motion.div>
    </ClaimFrame>
  );

  return (
    <ClaimFrame
      title="File a claim"
      /*
       * The intro depends on whether the merchant is already known. On a
       * merchant link it is a reassurance — this is scoped to your store. On
       * the generic route that sentence would be a lie until the shopper picks
       * one.
       */
      intro={needsStore
        ? 'Tell us what happened with your protected order. Enter your order number and the email you used at checkout — we find the rest.'
        : 'Tell us what happened with your protected order. This link is scoped to the store you bought from.'}
      supportEmail={supportEmail}
      /*
       * Suppressed when the host page already has an h1. Embedding the wizard
       * under the /file-a-claim hero otherwise puts two h1s on one page, which
       * a screen-reader user navigating by heading hears as two documents.
       */
      hideHeading={hideHeading}
    >
      {wizard.step !== 'verify' ? <ClaimGuidance step={wizard.step} artworkBaseUrl={artworkBaseUrl} /> : null}
      <Card>
        <ClaimProgress index={wizard.index} furthest={wizard.furthest} onSelect={wizard.goTo} />
        {error ? <Notice tone="error">{error}</Notice> : null}

        <AnimatePresence mode="wait" initial={false} custom={direction}>
        {wizard.step === 'verify' ? (
          <ClaimStepPanel key={wizard.step} stepId="verify" direction={direction} title="Find your order"
            // Enter in either field looks up the order, which is what a person
            // typing an order number expects and what the page previously
            // ignored. No precondition here on purpose: `verify()` already
            // refuses an incomplete pair and SAYS so, which is a better answer
            // to Enter than doing nothing silently.
            onSubmit={() => void verify()}
            hint={needsStore
              ? 'Enter the order number and the email address you used at checkout. We match them to your store automatically.'
              : 'Enter the order number and the email address you used at checkout. We check both against this store.'}>
            <div className="claim-grid">
              <Field label="Order number" required>
                <Input
                  value={orderNumber}
                  onChange={(e) => { setOrderNumber(e.currentTarget.value); setOrder(null); setResolvedSlug(null); }}
                  autoComplete="off"
                  placeholder="e.g. 1024"
                  aria-describedby={needsStore ? 'claim-lookup-hint' : undefined}
                />
              </Field>
              <Field label="Email address" required>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.currentTarget.value); setOrder(null); setResolvedSlug(null); }}
                  autoComplete="email"
                />
              </Field>
            </div>
            {needsStore ? (
              <p id="claim-lookup-hint" className="claim-lookup-hint">
                Use the order number exactly as it appears on your confirmation email or receipt.
              </p>
            ) : null}
            <ClaimStepActions
              onNext={() => void verify()}
              nextLabel="Find my order"
              nextBusy={busy === 'verify'}
              nextDisabled={!orderNumber.trim() || !email.trim()}
            />
          </ClaimStepPanel>
        ) : null}

        {/*
          * A later step with no order is a dead end, not a blank.
          *
          * `items` and `review` both render only `&& order`, and `order` is
          * cleared whenever the verify fields change — including by the browser
          * restoring a form, or by the shopper stepping back through the
          * progress bar to correct a typo and then forward again. The Card then
          * showed a progress bar and nothing else: no fields, no buttons, no
          * message. The only way out was a reload, which loses the claim.
          */}
        {(wizard.step === 'items' || wizard.step === 'review') && !order ? (
          <ClaimStepPanel
            key={wizard.step}
            stepId="verify"
            direction={direction}
            title="We need your order again"
            hint="Your order details were cleared. Find the order once more and we will bring you back here."
          >
            <ClaimStepActions onNext={() => wizard.goTo(0)} nextLabel="Find my order" />
          </ClaimStepPanel>
        ) : null}

        {wizard.step === 'items' && order ? (
          <ClaimStepPanel key={wizard.step} stepId="items" direction={direction} title="Which items were affected?"
            /*
             * Names the matched merchant on the generic route. The shopper
             * never chose a store, so this is the first and only place they can
             * confirm the lookup found the right one before describing what
             * happened.
             */
            hint={order.storeName
              ? `Order ${order.orderNumber} from ${order.storeName}. Choose the items this claim is about, and how many of each.`
              : `Order ${order.orderNumber}. Choose the items this claim is about, and how many of each.`}>
            <fieldset className="cw-items">
              <legend>Affected items</legend>
              {order.items.map((item) => {
                const isSelected = item.id in selected;
                return (
                  <div className="cw-item" key={item.id} data-selected={isSelected}>
                    <input
                      aria-label={item.title}
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        // Records that the choice is the SHOPPER's, so a later
                        // re-verification can say it reset one rather than
                        // silently replacing a default with a default.
                        hadSelection.current = true;
                        setSelected((current) => {
                          const next = { ...current };
                          if (checked) next[item.id] = 1; else delete next[item.id];
                          return next;
                        });
                      }}
                    />
                    <div className="cw-item__meta">
                      <span className="cw-item__title">{item.title}</span>
                      <span className="cw-item__sub">
                        {item.sku ? `SKU ${item.sku} · ` : ''}Ordered: {item.quantity}
                      </span>
                      {isSelected ? (
                        <div className="cw-item__qty">
                          <label htmlFor={`qty-${item.id}`}>Affected quantity (max {item.quantity})</label>
                          <input
                            id={`qty-${item.id}`}
                            className="ui-control"
                            aria-label={`Claimed quantity for ${item.title}`}
                            type="number"
                            min={1}
                            max={item.quantity}
                            step={1}
                            value={selected[item.id]}
                            onChange={(e) => {
                              const quantity = e.currentTarget.valueAsNumber;
                              setSelected((current) => ({ ...current, [item.id]: Number.isNaN(quantity) ? 0 : quantity }));
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                    <span className="cw-item__price">{formatClaimMoney(item.totalPrice, order.currency)}</span>
                  </div>
                );
              })}
            </fieldset>
            <ClaimStepActions
              onBack={wizard.back}
              onNext={wizard.next}
              nextDisabled={!selectedLines.length || !quantitiesValid}
            />
          </ClaimStepPanel>
        ) : null}

        {wizard.step === 'details' ? (
          <ClaimStepPanel key={wizard.step} stepId="details" direction={direction} title="What happened?"
            hint="Tell us what went wrong, in your own words.">
            <Field label="Reason" required>
              <Select value={reason} onChange={(e) => setReason(e.currentTarget.value)}>
                <option value="LOST">Lost in transit</option>
                <option value="DAMAGED">Damaged</option>
                <option value="STOLEN">Stolen</option>
                <option value="NOT_DELIVERED">Not delivered</option>
                <option value="OTHER">Other</option>
              </Select>
            </Field>
            <Field label="Description" required hint="Do not include passwords, payment-card details, or your tracking token.">
              <Textarea value={description} maxLength={5000} onChange={(e) => setDescription(e.currentTarget.value)} />
            </Field>
            <ClaimStepActions onBack={wizard.back} onNext={wizard.next} nextDisabled={!description.trim()} />
          </ClaimStepPanel>
        ) : null}

        {wizard.step === 'evidence' ? (
          <ClaimStepPanel key={wizard.step} stepId="evidence" direction={direction} title="Add supporting evidence"
            hint="Photos or documents help Enshield review your claim. You can also add these later from your tracking link.">
            <div className="claim-evidence-dropzone" data-testid="public-evidence-dropzone" onDragOver={(event) => event.preventDefault()} onDrop={dropEvidence}>
              <strong>Upload photos or files</strong>
              <span>Drag and drop images or PDFs here, or choose files from your device.</span>
              <label className="claim-file">Choose files<input aria-label="Upload photos or files" type="file" multiple accept={policy.acceptedContentTypes.join(',')} onChange={(event) => { selectEvidence(event.currentTarget.files ?? []); event.currentTarget.value = ''; }} /></label>
            </div>
            <QueuedEvidence items={queue} removable onRemove={(id) => dispatchQueue({ type: 'remove', id })} />
            {/* Evidence is optional — a claim with none is still a valid claim. */}
            <ClaimStepActions onBack={wizard.back} onNext={wizard.next} nextLabel={queue.length ? 'Continue' : 'Skip for now'} />
          </ClaimStepPanel>
        ) : null}

        {wizard.step === 'review' && order ? (
          <ClaimStepPanel key={wizard.step} stepId="review" direction={direction} title="Check your claim"
            hint="Enshield will review these details. Check that everything is accurate before submitting.">
            <div className="cw-review">
              <div className="cw-review__row">
                <span className="cw-review__key">Order</span>
                <span className="cw-review__value">{order.orderNumber} · {email}</span>
                <button type="button" className="cw-review__edit" onClick={() => wizard.goTo(0)}>Edit</button>
              </div>
              <div className="cw-review__row">
                <span className="cw-review__key">Affected items</span>
                <span className="cw-review__value">
                  {selectedLines.map((item) => `${item.title} × ${selected[item.id]}`).join(', ')}
                </span>
                <button type="button" className="cw-review__edit" onClick={() => wizard.goTo(1)}>Edit</button>
              </div>
              <div className="cw-review__row">
                <span className="cw-review__key">Reason</span>
                {/* The label, not the enum: "DAMAGED" is our vocabulary, not
                    the customer's. */}
                <span className="cw-review__value">{REASON_LABELS[reason] ?? reason}</span>
                <button type="button" className="cw-review__edit" onClick={() => wizard.goTo(2)}>Edit</button>
              </div>
              <div className="cw-review__row">
                <span className="cw-review__key">Description</span>
                <span className="cw-review__value">{description}</span>
                <button type="button" className="cw-review__edit" onClick={() => wizard.goTo(2)}>Edit</button>
              </div>
              <div className="cw-review__row">
                <span className="cw-review__key">Evidence</span>
                <span className="cw-review__value">
                  {queue.length ? queue.map((item) => item.file.name).join(', ') : 'None attached'}
                </span>
                <button type="button" className="cw-review__edit" onClick={() => wizard.goTo(3)}>Edit</button>
              </div>
            </div>
            {/*
              OWNER / LEGAL APPROVAL REQUIRED — placeholder wording. It states
              only what the platform verifiably does; the binding privacy notice
              must be supplied and approved before launch.
            */}
            <p className="cw-legal">
              Submitting sends your claim and attached files to Enshield for review.
              See the <a href={`${publicBaseUrl.replace(/\/$/, '')}/privacy-policy`}>Privacy Policy</a>.
            </p>
            <ClaimStepActions
              onBack={wizard.back}
              onNext={() => void submit()}
              nextLabel="Submit claim"
              nextBusy={busy === 'submit'}
              nextDisabled={!selectedLines.length || !quantitiesValid || !description.trim()}
            />
          </ClaimStepPanel>
        ) : null}
        </AnimatePresence>
      </Card>
      {wizard.step === 'verify' ? <ClaimGuidance step={wizard.step} artworkBaseUrl={artworkBaseUrl} /> : null}
    </ClaimFrame>
  );

}

export function ClaimTracking({ apiClient, supportEmail, uploadEvidence, hideHeading = false, trackingPlaceholder }: PublicClaimComponentProps & { uploadEvidence?: EvidenceUploader; hideHeading?: boolean; trackingPlaceholder?: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [activeToken, setActiveToken] = useState('');
  const [claim, setClaim] = useState<PublicClaimStatusDto | null>(null);
  const reducedMotion = useReducedMotion() ?? false;
  /*
   * The merchant's evidence policy, fetched exactly as the submission page
   * fetches it.
   *
   * This screen had none, so a shopper adding evidence to an existing claim was
   * validated against `DEFAULT_EVIDENCE_POLICY` — a compiled-in constant no
   * merchant configured. A file their store accepts was refused in the browser
   * and never reached them; a file it does not was uploaded and rejected by the
   * server with no warning the shopper could have acted on.
   */
  const [policy, setPolicy] = useState<EvidencePolicyDto>(DEFAULT_EVIDENCE_POLICY);

  const [busy, setBusy] = useState<'track' | 'upload' | 'contact' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const handledLocation = useRef<string | null>(null);
  const claimGeneration = useRef(0);
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [message, setMessage] = useState('');

  const track = useCallback(async (value: string) => {
    /*
     * The field invites a LINK, so accept one.
     *
     * It posted the value verbatim, and a full URL is 20–200 characters — so it
     * passed validation, missed the hash lookup, and the shopper was told their
     * link was invalid or expired for doing exactly what the placeholder asked.
     */
    const normalized = tokenFromPastedValue(value);
    if (!normalized) { setError('Enter your tracking token.'); return; }
    const generation = ++claimGeneration.current;
    setClaim(null);
    setActiveToken('');
    setUploadProgress(0);
    setName('');
    setEmail('');
    setMessage('');
    setBusy('track'); setError(null); setNotice(null);
    try {
      const trackedClaim = await apiClient.post<PublicClaimStatusDto>(PUBLIC_CLAIM_ENDPOINTS.track, { token: normalized });
      if (claimGeneration.current !== generation) return;
      setClaim(trackedClaim); setActiveToken(normalized); setToken('');
    }
    catch (cause) { if (claimGeneration.current === generation) setError(messageFor(cause, 'Unable to look up that claim.')); }
    finally { if (claimGeneration.current === generation) setBusy(null); }
  }, [apiClient]);

  useEffect(() => {
    let active = true;
    Promise.resolve(apiClient.get<EvidencePolicyDto>(PUBLIC_CLAIM_ENDPOINTS.evidencePolicy))
      .then((next) => { if (active && next) setPolicy(next); })
      // Silently keeping the default is right here: a shopper must still be
      // able to attach evidence when the policy endpoint is unreachable.
      .catch(() => undefined);
    return () => { active = false; };
  }, [apiClient]);

  useEffect(() => {
    const key = `${location.pathname}${location.search}${location.hash}`;
    if (handledLocation.current === key) return;
    handledLocation.current = key;
    const handedOff = consumeTrackingTokenFromUrl(new URL(key, window.location.origin), (cleanUrl) => navigate(cleanUrl, { replace: true, state: null }));
    if (handedOff) void track(handedOff);
  }, [location.hash, location.pathname, location.search, navigate, track]);

  const upload = async (file?: File) => {
    if (!file || !activeToken) return;
    const generation = claimGeneration.current;
    // Same policy the submission page uses. This screen had none at all, so a
    // shopper adding evidence to an existing claim was validated against a
    // compiled-in constant rather than against the deployment's own limits.
    const validation = validateEvidenceFile(file, policy); if (validation) { setError(validation); return; }
    const form = new FormData(); form.append('token', activeToken); form.append('file', file);
    setBusy('upload'); setUploadProgress(0); setError(null);
    try {
      const uploader = uploadEvidence ?? ((path: string, body: FormData, progress: (value: number) => void) => uploadEvidenceWithProgress<PublicClaimStatusDto>(apiClient.url(path), body, progress));
      const updatedClaim = await uploader(PUBLIC_CLAIM_ENDPOINTS.evidence, form, (value) => {
        if (claimGeneration.current === generation) setUploadProgress(value);
      });
      if (claimGeneration.current !== generation) return;
      setClaim(updatedClaim); setNotice(`${file.name} was attached to your claim.`);
    }
    catch (cause) { if (claimGeneration.current === generation) setError(messageFor(cause, 'Unable to attach that file.')); }
    finally { if (claimGeneration.current === generation) { setBusy(null); setUploadProgress(0); } }
  };

  const contact = async (event: FormEvent) => {
    event.preventDefault(); if (!name.trim() || !email.trim() || !message.trim()) { setError('Complete every contact field.'); return; }
    const generation = claimGeneration.current;
    setBusy('contact'); setError(null);
    try {
      await apiClient.post(PUBLIC_CLAIM_ENDPOINTS.supportContact, { token: activeToken, name: name.trim(), email: email.trim(), message: message.trim() });
      if (claimGeneration.current !== generation) return;
      setNotice('Your message was received. The support team will reply by email.'); setMessage('');
    }
    catch (cause) { if (claimGeneration.current === generation) setError(messageFor(cause, 'Unable to send your message.')); }
    finally { if (claimGeneration.current === generation) setBusy(null); }
  };

  return (
    <ClaimFrame title="Track your claim" intro="Enter the private token from your claim confirmation or email link. It is used only to retrieve this claim." supportEmail={supportEmail} hideHeading={hideHeading}>
      <Card>
        <form className="claim-form" onSubmit={(e) => { e.preventDefault(); void track(token); }}>
          {error ? <Notice tone="error">{error}</Notice> : null}{notice ? <Notice tone="success">{notice}</Notice> : null}
          <Field label="Tracking token" required><Input value={token} onChange={(e) => setToken(e.currentTarget.value)} autoComplete="off" placeholder={trackingPlaceholder} /></Field>
          <Button type="submit" busy={busy === 'track'}>Track claim</Button>
        </form>
      </Card>
      <AnimatePresence initial={false}>
      {claim ? <motion.div
        key={claim.reference}
        data-testid="claim-tracking-result"
        data-motion="claim-tracking-result"
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={reducedMotion ? undefined : { opacity: 0 }}
        transition={{ duration: reducedMotion ? 0 : claimMotion.duration.interface, ease: claimMotion.ease.standard }}
      >
        <Card><div className="claim-result-heading"><div><p className="claim-kicker">Claim reference</p><h2>{claim.reference}</h2></div><strong className="claim-status">{claim.status.replace(/_/g, ' ')}</strong></div>
          <dl className="claim-details"><div><dt>Store</dt><dd>{claim.storeName || '—'}</dd></div><div><dt>Order</dt><dd>{claim.orderNumber ?? '—'}</dd></div><div><dt>Claimed</dt><dd>{claim.claimedAmount.toFixed(2)} {claim.currency}</dd></div><div><dt>Approved</dt><dd>{claim.approvedAmount === null ? 'Not decided yet' : `${claim.approvedAmount.toFixed(2)} ${claim.currency}`}</dd></div><div><dt>Submitted</dt><dd>{new Date(claim.submittedAt).toLocaleString()}</dd></div>{claim.decidedAt ? <div><dt>Decided</dt><dd>{new Date(claim.decidedAt).toLocaleString()}</dd></div> : null}{claim.completedAt ? <div><dt>Completed</dt><dd>{new Date(claim.completedAt).toLocaleString()}</dd></div> : null}</dl>
          {claim.description ? <p className="claim-description">{claim.description}</p> : null}
          {guidanceForStatus(claim) ? <Notice tone={claim.status === 'APPROVED' || claim.status === 'COMPLETED' ? 'success' : claim.status === 'AWAITING_EVIDENCE' ? 'warning' : claim.status === 'DENIED' ? 'error' : 'info'}>{guidanceForStatus(claim)}</Notice> : null}
          {claim.timeline.length ? <ol className="claim-timeline">{claim.timeline.map((item) => <li key={item.id}><strong>{timelineEventLabel(item.eventType)}</strong><p>{item.message}</p><time dateTime={item.createdAt}>{new Date(item.createdAt).toLocaleString()}</time></li>)}</ol> : <p className="claim-empty">No claim updates yet. New review activity will appear here.</p>}
          {claim.notes.length ? <section><h3>Claim messages</h3><ul className="claim-list">{claim.notes.map((note) => <li key={note.id}><time dateTime={note.createdAt}>{new Date(note.createdAt).toLocaleString()}</time><p>{note.body}</p></li>)}</ul></section> : null}
        </Card>
        <Card><h2>Your evidence ({claim.evidenceCount})</h2><EvidenceList claim={claim} />{busy === 'upload' ? <progress aria-label="Evidence upload progress" aria-valuenow={uploadProgress} value={uploadProgress} max={100}>{uploadProgress}%</progress> : null}{claim.canUploadEvidence ? <label className="claim-file">Attach a photo or document<input aria-label="Attach a photo or document to this claim" type="file" accept={policy.acceptedContentTypes.join(',')} disabled={busy === 'upload'} onChange={(e) => { const file = e.currentTarget.files?.[0]; e.currentTarget.value = ''; void upload(file); }} /></label> : <p className="claim-empty">This claim is closed and is no longer accepting files.</p>}</Card>
        <Card><h2>Contact support about this claim</h2><form className="claim-form" onSubmit={contact}><div className="claim-grid"><Field label="Your name" required><Input value={name} onChange={(e) => setName(e.currentTarget.value)} autoComplete="name" /></Field><Field label="Your email" required><Input type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} autoComplete="email" /></Field></div><Field label="Message" required><Textarea value={message} onChange={(e) => setMessage(e.currentTarget.value)} /></Field><Button type="submit" busy={busy === 'contact'}>Send claim message</Button></form></Card>
      </motion.div> : null}
      </AnimatePresence>
    </ClaimFrame>
  );
}
